import * as React from 'react';
import {useEffect, useMemo, useRef} from 'react';
import TextField from '@mui/material/TextField';
import {
	EntityType,
	FormData,
	FormSelectOptions,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionStringEntry,
	SelectOption,
} from '../types';
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
	useTheme,
} from '@mui/material';
import {css} from '@emotion/css';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import ClearIcon from '@mui/icons-material/Clear';

/** Hides the resting underline of filled inputs inside the styled element; the focus underline still shows. */
export const hideIdleInputUnderlineSx = {
	'& .MuiFilledInput-underline::before': { display: 'none' },
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

interface SelectFieldProps {
	name: string,
	slug: string,
	label?: string,
	selectOptions: SelectOption[],
	defaultValue: string,
	value?: string;
	required?: boolean,
	onValueChange?: (event: SelectChangeEvent<string>) => void;
	hideLabel?: boolean,
	variant: 'standard' | 'filled',
}

interface SelectMultipleFieldProps {
	name: string,
	slug: string,
	label?: string,
	selectOptions: SelectOption[],
	defaultValue: string[],
	value?: string[],
	onValueChange?: (event: SelectChangeEvent<any>) => void;
	required?: boolean,
	disabled?: boolean,
}

type SyntheticEventTarget = EventTarget & { checked: boolean };

const SelectField = ({name, slug, label, selectOptions, onValueChange, defaultValue, value, required, variant }: SelectFieldProps) => {
	const labelId = label ? `${slug}-select-label` : undefined;
	return <div>
		<FormControl fullWidth variant={variant} margin="dense" required={required}>
			{label && <InputLabel id={labelId}>{label}</InputLabel>}
			<Select
				name={name}
				labelId={labelId}
				defaultValue={defaultValue}
				value={value}
				onChange={onValueChange}
			>
				{selectOptions.map((selectOption) => <MenuItem key={selectOption.value} value={selectOption.value ?? ''}>{selectOption.label}</MenuItem> )}
				{!required && (<MenuItem value=''>Unassigned</MenuItem>)}
			</Select>
		</FormControl>
	</div>;
};

const SelectMultipleField = ({name, slug, label, selectOptions, onValueChange, defaultValue, value, required, disabled }: SelectMultipleFieldProps) => {
	const optionLabelsMap = selectOptions.reduce((acc, option) => acc.set(option.value, option.label), new Map<string, string>());
	const labelId = label ? `${slug}-multiple-checkbox-label` : undefined;
	return <div>
		<FormControl fullWidth variant="standard" margin="dense" required={required} disabled={disabled}>
			{label && <InputLabel id={labelId}>{label}</InputLabel>}
			<Select
				labelId={labelId}
				name={name}
				multiple
				defaultValue={defaultValue ?? []}
				value={value}
				onChange={onValueChange}
				renderValue={(selected) => selected.map((val) => optionLabelsMap.get(val)).join(', ')}
				MenuProps={MenuProps}
			>
				{selectOptions.map((selectOption: any) => (
					<MenuItem key={selectOption.value} value={selectOption.value}>
						<Checkbox icon={<PushPinOutlinedIcon fontSize="small" />} checkedIcon={<PushPinIcon fontSize="small" />} checked={defaultValue && defaultValue.indexOf(selectOption.value) > -1} />
						<ListItemText primary={selectOption.label} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	</div>;
};

interface Props {
	formData: FormData;
	propertyDefinitions: PropertyDefinition[];
	onValueChange?: (key: string, value: any) => void;
	readOnly?: boolean;
	selectOptions?: FormSelectOptions;
	autoFocusSlug?: string;
	hideLabel?: boolean;
	disableAutoFocus?: boolean;
	textFieldVariant?: 'standard' | 'filled';
}

function FormTemplate({
	propertyDefinitions,
	formData,
	onValueChange,
	selectOptions,
	readOnly,
	autoFocusSlug,
	hideLabel,
	disableAutoFocus,
	textFieldVariant = 'filled',
}: Props) {
	const autoFocusRef = useRef<HTMLInputElement | undefined>(undefined);
	const hasFocusedRef = useRef(false);
	const theme = useTheme();
	const dateInputRef = useRef<HTMLInputElement | undefined>(undefined);

	const classes = {
		booleanField: css`
			margin-top: ${theme.spacing(2)};
			user-select: none;
		`
	};

	// enable auto-focus on first property definition that has no default value
	const autoFocusPropertyDef = useMemo(
		() => (disableAutoFocus ? undefined : propertyDefinitions.find((def) => autoFocusSlug === def.slug || !formData[def.slug])),
		[propertyDefinitions, formData, autoFocusSlug, disableAutoFocus],
	);

	useEffect(() => {
		if (!propertyDefinitions.length || hasFocusedRef.current) {
			return;
		}
		const timeout = setTimeout(() => {
			if (autoFocusRef.current) {
				(autoFocusRef.current as HTMLInputElement).focus();
				hasFocusedRef.current = true;
			}
		}, 100);
		return () => {
			clearTimeout(timeout);
		};
	// Only depend on length so we run once when definitions first arrive.
	}, [propertyDefinitions.length]);

	return <FormGroup>{propertyDefinitions.map((def) => {
		const {type, slug, label, required} = def;
		const defaultValue = formData[slug];
		const shouldAutoFocus = autoFocusPropertyDef?.slug === slug;
		
		switch (type) {
		case EntityType.PropertyDefinitionString: {
			const {disabled: propertyDisabled, placeholder} = def as PropertyDefinitionStringEntry;
			const disabled = Boolean(propertyDisabled ?? readOnly);
			return <TextField
				name={slug}
				key={slug}
				required={required}
				margin="dense"
				id={slug}
				label={hideLabel ? undefined : label}
				type="text"
				fullWidth
				variant={textFieldVariant}
				defaultValue={defaultValue}
				onChange={onValueChange ? (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.name, e.target.value) : undefined}
				disabled={disabled}
				placeholder={placeholder}
				slotProps={
					shouldAutoFocus ? {
						htmlInput: {
							ref: autoFocusRef,
						},
					} : undefined
				}
				autoComplete='off'
			/>;
		}
		
		case EntityType.PropertyDefinitionNumber: {
			return <TextField
				label={hideLabel ? undefined : label}
				name={slug}
				key={slug}
				required={required}
				margin="normal"
				type="number"
				fullWidth
				variant="standard"
				defaultValue={defaultValue}
				onChange={onValueChange ? (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.name, e.target.value) : undefined}
				disabled={readOnly}
				slotProps={
					shouldAutoFocus ? {
						htmlInput: {
							ref: autoFocusRef,
						},
					} : undefined
				}
			/>;
		}
		case EntityType.PropertyDefinitionText: {
			return <TextField
				name={slug}
				key={slug}
				required={required}
				margin="dense"
				multiline
				label={hideLabel ? undefined : label}
				type="text"
				fullWidth
				variant={textFieldVariant}
				defaultValue={defaultValue}
				onChange={onValueChange ? (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.name, e.target.value) : undefined}
				disabled={readOnly}
				autoComplete='off'
				slotProps={
					shouldAutoFocus ? {
						htmlInput: {
							ref: autoFocusRef,
						},
					} : undefined
				}
			/>;
		}
		case EntityType.PropertyDefinitionBoolean: {
			const disabled = Boolean(readOnly);
			return <FormControl fullWidth variant="standard" required={required} margin='dense'>
				<FormControlLabel
					name={slug}
					key={slug}
					sx={{my: 0}}
					control={<Checkbox defaultChecked={defaultValue as boolean} />}
					label={label}
					onChange={onValueChange ? (e: React.SyntheticEvent) => onValueChange((e.target as HTMLInputElement).name, (e.target as SyntheticEventTarget).checked) : undefined}
					className={classes.booleanField}
					disabled={disabled}
				/></FormControl>;
		}
		case EntityType.PropertyDefinitionBelongsTo: {
			const disabled = Boolean(readOnly);
			const _selectOptions = selectOptions ? selectOptions[slug] : [];
			if ((def as PropertyDefinitionBelongsTo).allowMany) {
				return <SelectMultipleField
					name={slug}
					key={slug}
					slug={slug}
					label={hideLabel ? undefined : label}
					selectOptions={_selectOptions}
					onValueChange={onValueChange ? (event: SelectChangeEvent<any>) => onValueChange(slug, event.target.value) : undefined}
					defaultValue={defaultValue as string[]}
					required={required}
					disabled={disabled}
				/>;
			} else {
				return <SelectField
					name={slug}
					key={slug}
					slug={slug}
					label={hideLabel ? undefined : label}
					selectOptions={_selectOptions}
					onValueChange={onValueChange ? (event: SelectChangeEvent<string>) => onValueChange(slug, event.target.value) : undefined}
					defaultValue={defaultValue as string}
					required={required}
					variant={textFieldVariant}
				/>;
			}
		}
		
		case EntityType.PropertyDefinitionDate: {
			const clearDate = () => {
				onValueChange ? onValueChange(slug, '') : () => {return;};
				if (autoFocusRef?.current) {
					autoFocusRef.current.value = '';
				}
				if (dateInputRef.current) {
					dateInputRef.current.value = '';
				}
			};
			return <FormControl fullWidth variant="standard" required={required} margin='dense'>
				{/* <InputLabel shrink>{label}</InputLabel> */}
				<Box display='flex' gap={1} alignItems="center">
					<TextField
						name={slug}
						key={slug}
						required={required}
						type="date"
						margin='none'
						fullWidth
						variant={textFieldVariant}
						defaultValue={defaultValue}
						onChange={onValueChange ? (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.name, e.target.value) : undefined}
						disabled={readOnly}
						label={label}
						slotProps={{
							htmlInput: {ref: shouldAutoFocus && autoFocusRef ? autoFocusRef : dateInputRef},
							inputLabel: {shrink: true}
						}}
					/>
					{!required && <IconButton
						type="button"
						aria-label="Clear date"
						onClick={clearDate}
						disabled={readOnly}
						sx={{
							mr: -0.5,
							mt: 1,
						}}
					>
						<ClearIcon fontSize="small" />
					</IconButton>}
				</Box>
			</FormControl>;
		}
		
		case EntityType.PropertyDefinitionDatetime: {
			const clearDate = () => {
				onValueChange ? onValueChange(slug, '') : () => {return;};
				if (autoFocusRef?.current) {
					autoFocusRef.current.value = '';
				}
				if (dateInputRef.current) {
					dateInputRef.current.value = '';
				}
			};
			return <FormControl fullWidth variant="standard" required={required} margin='normal'>
				<InputLabel shrink>{label}</InputLabel>
				<Box display='flex' gap={1} alignItems="center">
					<TextField
						name={slug}
						key={slug}
						required={required}
						type="datetime-local"
						margin='normal'
						fullWidth
						variant="standard"
						defaultValue={defaultValue}
						onChange={onValueChange ? (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.name, e.target.value) : undefined}
						disabled={readOnly}
						slotProps={{
							htmlInput: {ref: shouldAutoFocus && autoFocusRef ? autoFocusRef : dateInputRef},
						}}
					/>
					{!required && <IconButton
						type="button"
						aria-label="Clear date"
						onClick={clearDate}
						disabled={readOnly}
						sx={{
							mr: -0.5,
							mt: 1,
						}}
					>
						<ClearIcon fontSize="small" />
					</IconButton>}
				</Box>
			</FormControl>;
		}
		
		default:
			return null;
		}
	})}</FormGroup>;
}

export default FormTemplate;